/**
 * Negotiation Tactics Scenario — 40 Characters
 *
 * Unlike Cold Call Mastery (first 30 seconds) or Objection Defense (handling "no"),
 * Negotiation Tactics begins AFTER interest is established. The deal is on the table.
 * The prospect wants your product — they just want it cheaper, faster, or on better terms.
 *
 * Your job: hold your position, trade intelligently, and close without giving away margin.
 *
 * 4 sub-categories:
 *   1. price-anchor       — Setting and defending the first number
 *   2. contract-terms     — Pushing back on unfavorable clauses
 *   3. multi-stakeholder  — Navigating rooms with conflicting agendas
 *   4. final-offer-defense — Holding your line on the last round
 *
 * silenceProfile.stages[] controls hangup timing:
 *   - type: "warn" | "hangup"
 *   - delay: milliseconds of silence before triggering
 *   - lineKey: key in character.lines to speak
 *   - (audio is looked up by lineKey: see ../audio.js and audioManifest.json)
 */

export const negotiationTactics = {

  // ═══════════════════════════════════════════════════════════════
  // BEGINNER (10 characters)
  // ═══════════════════════════════════════════════════════════════

  // 1. FRANK DELILLO — The Anchoring Veteran
  'frank-delillo-vp-of-procurement': {
    id: 'frank-delillo-vp-of-procurement',
    name: 'Frank DeLillo',
    role: 'VP of Procurement',
    company: 'Atlantic Manufacturing',
    industry: 'manufacturing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/frank-delillo-vp-of-procurement.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Anchoring Veteran',
    scenarioDesc: "Frank is 57, Italian-American, and opens every negotiation by throwing out a number 40% below your quote. He's done this for 30 years. He expects you to counter. If you accept his anchor, he loses respect for you and your product.",
    goal: 'Re-anchor the negotiation at your price without losing the deal.',
    category: 'negotiation-tactics',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I've looked at your proposal. We'll do $42K. That's our number.",
      impatient1: "Look, $42K is generous. Take it or leave it.",
      impatient2: "I have two other vendors at that range. Don't overthink this.",
      hangup: "Going with the other bid. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't counter immediately. Ask what he's comparing to. Break his anchor by changing the unit of measurement — cost per user, cost per outcome, not total sticker.",
      commonMistake: "Splitting the difference. He anchored at $42K hoping you'd land at $55K. Your quote was $72K. Splitting = you lost $17K for free.",
      winningMove: "Say: 'Frank, I appreciate the directness. But $42K gets you our entry tier — no SLA, no dedicated support. For what you actually need, we're at $68K. Where do you want to land on the feature set?'"
    }
  },

  // 2. HELEN CHO — The Reasonable Negotiator
  'helen-cho-director-of-operations': {
    id: 'helen-cho-director-of-operations',
    name: 'Helen Cho',
    role: 'Director of Operations',
    company: 'PacificRim Logistics',
    industry: 'logistics',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/helen-cho-director-of-operations.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Fair Dealer',
    scenarioDesc: "Helen is 43, Korean-American, and negotiates in good faith. She genuinely wants a fair deal for both sides. She's reasonable — but she's also sharp. She'll find every hidden cost in your proposal and surface it. Fairness doesn't mean easy.",
    goal: 'Close a deal both sides feel good about.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'warn', delay: 14000, lineKey: 'impatient2' },
        { type: 'hangup', delay: 20000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I like your product. Let's find a number that works for both of us. Walk me through the pricing breakdown.",
      impatient1: "I notice implementation is listed separately. Is that negotiable or fixed?",
      impatient2: "I want to be fair, but I need to understand every line item before I sign.",
      hangup: "I can't sign something I don't fully understand. Let's reconnect when you have a cleaner proposal. *click*"
    },
    coachingNotes: {
      whatWorks: "Be completely transparent. She rewards honesty with speed. Hide costs and she'll slow the deal to a crawl.",
      commonMistake: "Burying fees in the proposal. She will find them and it will destroy trust.",
      winningMove: "Say: 'Helen, here's the full breakdown — no hidden fees. Implementation is $8K, annual license is $36K, support is included. The only variable is if you need custom integrations.'"
    }
  },

  // 3. MARCUS BELL — The Good Cop
  'marcus-bell-sales-director': {
    id: 'marcus-bell-sales-director',
    name: 'Marcus Bell',
    role: 'Sales Director',
    company: 'Brightwave Media',
    industry: 'media',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/marcus-bell-sales-director.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Good Cop',
    scenarioDesc: "Marcus is 41, African-American, and plays the sympathetic negotiator. He's on your side — he wants this deal. But his CFO is the bad cop. Every concession he asks for is framed as 'my CFO needs this, not me.' The CFO may or may not exist.",
    goal: 'Identify whether the CFO is real or a negotiation tactic.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Look, I love this. I'm sold. But my CFO... she's going to need at least 15% off or she'll kill it.",
      impatient1: "I'm fighting for you internally, but she's tough. Help me help you.",
      impatient2: "If I go back with this number, she's going to shut me down.",
      hangup: "I tried. She said no. Sorry, man. *click*"
    },
    coachingNotes: {
      whatWorks: "Ask to include the CFO on the next call. If Marcus resists, the CFO is a phantom. If he agrees, prepare a CFO-specific pitch.",
      commonMistake: "Giving the discount to Marcus without verifying the CFO exists. You just got played by the oldest trick in the book.",
      winningMove: "Say: 'Marcus, I want to make this work. Can we get 30 minutes with your CFO? I'll build the ROI case specifically for her concerns. That way you're not stuck in the middle.'"
    }
  },

  // 4. DIANA REEVES — The Contract Redliner
  'diana-reeves-general-counsel': {
    id: 'diana-reeves-general-counsel',
    name: 'Diana Reeves',
    role: 'General Counsel',
    company: 'Irongate Financial',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/diana-reeves-general-counsel.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Clause Killer',
    scenarioDesc: "Diana is 50, sharp, and has redlined your contract into oblivion. She's changed the liability cap, the indemnification clause, the termination window, and the SLA penalties. She's not trying to kill the deal — she's trying to protect her company. But she'll take everything you give.",
    goal: "Protect your key contract terms while conceding the ones that don't matter.",
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'warn', delay: 14000, lineKey: 'impatient2' },
        { type: 'hangup', delay: 20000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I've reviewed your contract. I have 14 redlines. Let's start with the liability cap — it needs to be uncapped.",
      impatient1: "Section 7.3 is non-negotiable for us. Uncapped liability or no deal.",
      impatient2: "I've done this a thousand times. These are standard protections. Don't fight me on standard terms.",
      hangup: "If you can't accept standard legal protections, we need a different vendor. *click*"
    },
    coachingNotes: {
      whatWorks: "Know which clauses are expendable vs critical. Give her the easy wins first — termination window, notice period. Hold firm on liability cap and indemnification.",
      commonMistake: "Fighting every redline equally. You'll lose the war by winning battles that don't matter.",
      winningMove: "Say: 'Diana, I can move on 11 of your 14 points today. The three I need to hold are liability cap, indemnification scope, and SLA credits. Let me explain why each protects both of us.'"
    }
  },

  // 5. TOMMY VASQUEZ — The Small Business Haggler
  'tommy-vasquez-owner': {
    id: 'tommy-vasquez-owner',
    name: 'Tommy Vasquez',
    role: 'Owner',
    company: "Tommy's Auto Electric",
    industry: 'automotive',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/full-negotiation/tommy-vasquez-owner.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Street Negotiator',
    scenarioDesc: "Tommy is 52, Mexican-American, runs a 3-bay auto shop. He negotiates everything — parts, rent, even his lunch order. He's not trying to rip you off. He just can't help himself. He needs to feel like he won something or he can't sign.",
    goal: 'Give him a win without discounting your core price.',
    category: 'negotiation-tactics',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Alright, I like it. But what's the best you can do? Come on, give me something.",
      impatient1: "Every vendor gives me a little something extra. What's yours?",
      impatient2: "I just need to feel like I'm getting a deal here.",
      hangup: "No deal sweetener, no deal. I'll find another guy. *click*"
    },
    coachingNotes: {
      whatWorks: "Give him a non-monetary win — free training session, extended warranty, priority support for 90 days. He needs the story, not the discount.",
      commonMistake: "Dropping your price. He'll tell every business owner on the block you're flexible, and you'll never sell at list again.",
      winningMove: "Say: 'Tommy, price stays at $299/mo. But I'll throw in a free onsite training for your crew — that's normally $500. You're getting a deal nobody else gets.'"
    }
  },

  // 6. CATHERINE WELLS — The Annual vs Monthly
  'catherine-wells-finance-manager': {
    id: 'catherine-wells-finance-manager',
    name: 'Catherine Wells',
    role: 'Finance Manager',
    company: 'Summit Health Partners',
    industry: 'healthcare',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/catherine-wells-finance-manager.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Payment Poker',
    scenarioDesc: "Catherine is 38, wants your product but insists on monthly billing. Your company strongly prefers annual. She knows annual gives you cash flow and she's using that as leverage. She'll pay annual — but only if the discount is right.",
    goal: 'Convert her to annual billing with a justified discount.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "We want monthly billing. $4,200 a month. No annual commitment.",
      impatient1: "Annual locks us in. What if the product doesn't deliver?",
      impatient2: "Give me a reason to commit for 12 months that isn't just a discount.",
      hangup: "Monthly or nothing. Call me when you're flexible. *click*"
    },
    coachingNotes: {
      whatWorks: "Offer a meaningful annual discount (10-15%) AND address the risk — add a 90-day exit clause or a performance guarantee.",
      commonMistake: "Giving a 20% annual discount without getting anything in return. You just trained her to ask for more next year.",
      winningMove: "Say: 'Catherine, annual saves you 12% — that's $6K back. And I'll add a 90-day performance clause: if we miss our SLA targets in the first quarter, you can exit penalty-free. Fair?'"
    }
  },

  // 7. DEREK STONE — The Walk-Away Bluffer
  'derek-stone-ceo': {
    id: 'derek-stone-ceo',
    name: 'Derek Stone',
    role: 'CEO',
    company: 'Stonewall Creative',
    industry: 'marketing',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/full-negotiation/derek-stone-ceo.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Walk-Away Artist',
    scenarioDesc: "Derek is 44, runs a creative agency, and his signature move is standing up from the table. 'I'll walk away right now.' He does this in every negotiation. He's never actually walked away from a deal he wanted. It's pure theater. But if you panic, you lose.",
    goal: 'Call his bluff without being aggressive.',
    category: 'negotiation-tactics',
    personalityType: 'hostile',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I'm going to be straight with you — if this isn't the right number, I'm walking. I have other options.",
      impatient1: "I'm serious. I'll walk right now. Last chance.",
      impatient2: "You're losing this deal in real time. Make a move.",
      hangup: "I'm out. Don't call me. *click*"
    },
    coachingNotes: {
      whatWorks: "Stay calm. Acknowledge his power to walk. Then refocus on why he's still here — because he wants your product. People who are truly leaving don't announce it.",
      commonMistake: "Panicking and dropping price. The bluff worked. He'll use this move every time now.",
      winningMove: "Say: 'Derek, I completely respect that. If you need to walk, I understand. But you're still on the phone, which tells me this matters to you. What would need to change for this to work?'"
    }
  },

  // 8. NADIA KHOURY — The Scope Creeper
  'nadia-khoury-product-director': {
    id: 'nadia-khoury-product-director',
    name: 'Nadia Khoury',
    role: 'Product Director',
    company: 'Zenith Software',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/nadia-khoury-product-director.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Scope Creeper',
    scenarioDesc: "Nadia is 36, Lebanese-American, and her tactic is to agree on a price then slowly add scope. 'Oh, and can you also include data migration? And API access? And a dedicated CSM?' Each add-on is small alone but cumulatively massive. She genuinely doesn't realize she's doing it.",
    goal: 'Lock scope before locking price.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Great, $48K works. Oh — that includes data migration, right? And API access? And a dedicated success manager?",
      impatient1: "I thought these were standard. Every other vendor includes them.",
      impatient2: "If we're paying $48K, I'd expect full-service. We're a big account.",
      hangup: "Nickel-and-diming isn't a good look. I'll look elsewhere. *click*"
    },
    coachingNotes: {
      whatWorks: "Pause the deal. Write down every item she's adding and show the cumulative cost. Then let her prioritize. She doesn't realize she's creeping scope — make it visible.",
      commonMistake: "Saying 'sure, we'll include that' to each small ask. By the end you've given away $15K of services.",
      winningMove: "Say: 'Nadia, I want to make sure we're aligned. Let me list everything you've just asked for — migration, API, CSM. That's an additional $12K of scope. Which two are must-haves vs nice-to-haves?'"
    }
  },

  // 9. ROB PATTERSON — The Handshake Closer
  'rob-patterson-owner': {
    id: 'rob-patterson-owner',
    name: 'Rob Patterson',
    role: 'Owner',
    company: 'Patterson Plumbing & HVAC',
    industry: 'construction',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/full-negotiation/rob-patterson-owner.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Handshake Deal',
    scenarioDesc: "Rob is 49, old-school contractor. He hates contracts. He wants a handshake and a start date. He's trustworthy but you need a signed agreement for your own protection. Getting him to sign without making him feel like you don't trust him is the challenge.",
    goal: 'Get a signed contract without insulting his handshake culture.',
    category: 'negotiation-tactics',
    personalityType: 'direct',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "We got a deal? Great. When do we start? I don't need a bunch of paperwork.",
      impatient1: "A contract? Come on, my word is my bond. Ask anyone in this town.",
      impatient2: "If you can't trust a handshake, we got a problem.",
      hangup: "Too much red tape. I'll find someone who trusts people. *click*"
    },
    coachingNotes: {
      whatWorks: "Frame the contract as protecting HIM, not you. 'This locks in your price so we can never raise it on you.'",
      commonMistake: "Saying 'our legal department requires it.' He'll feel like a number, not a partner.",
      winningMove: "Say: 'Rob, your handshake is gold. The paperwork is just so nobody at my company can change the terms on you later. It protects YOUR price.'"
    }
  },

  // 10. LAURA CHEN — The Multi-Year Lock
  'laura-chen-cfo': {
    id: 'laura-chen-cfo',
    name: 'Laura Chen',
    role: 'CFO',
    company: 'NovaTech Solutions',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/laura-chen-cfo.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Multi-Year Play',
    scenarioDesc: "Laura is 42, Taiwanese-American, and wants a 3-year deal at year-one pricing with no escalator. She knows multi-year gives you revenue certainty — and she's using that as leverage. She's offering commitment but demanding you freeze your prices for 36 months.",
    goal: 'Negotiate a fair multi-year deal with reasonable escalators.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "We'll commit to 3 years. But we want year-one pricing locked for all three. No escalation.",
      impatient1: "A 3-year commitment is worth a LOT to your company. Price the certainty.",
      impatient2: "If you can't lock pricing, we'll do annual and re-evaluate every year.",
      hangup: "Going annual then. You lost the multi-year. *click*"
    },
    coachingNotes: {
      whatWorks: "Offer a small year-one discount (5-8%) but include a modest escalator (3-5%) in years 2 and 3. She gets a deal, you get protected margins.",
      commonMistake: "Locking flat pricing for 3 years. Inflation and feature additions will eat your margin.",
      winningMove: "Say: 'Laura, I'll give you 8% off year one as a multi-year reward. Years 2 and 3 step up 4% each — still below what we charge new customers. Over 3 years you save $22K vs annual.'"
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // INTERMEDIATE (16 characters)
  // ═══════════════════════════════════════════════════════════════

  // 11. RICHARD HARMON — The Incumbent Defender
  'richard-harmon-it-director': {
    id: 'richard-harmon-it-director',
    name: 'Richard Harmon',
    role: 'IT Director',
    company: 'Cascade Health Systems',
    industry: 'healthcare',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/richard-harmon-it-director.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Renewal Trap',
    scenarioDesc: "Richard is 54, and you're his incumbent vendor. He likes you but is using the renewal cycle to renegotiate aggressively. He's gotten three competing bids — real ones — and is threatening to switch unless you match. You need to hold margin while keeping the account.",
    goal: 'Retain the account without matching the lowest competitor bid.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "Renewal time. I have three bids on my desk. Two are 20% below your price. Make your case or I'm switching.", impatient1: "Switching costs are real but so is a 20% saving. Justify the premium.", impatient2: "I've used you for 4 years. Loyalty doesn't pay my bills.", hangup: "Going with the cheaper option. Sorry. *click*" },
    coachingNotes: { whatWorks: "Quantify the switching cost and the value of institutional knowledge. Then offer a modest loyalty discount (5-8%) — not matching the floor.", commonMistake: "Matching the lowest bid. You've set a new baseline and you'll never get the margin back.", winningMove: "Say: 'Richard, switching costs you roughly $80K in migration, retraining, and 3 months of productivity loss. I'll give you 7% loyalty discount — you still save more than switching when you factor in the hidden costs.'" }
  },

  // 12. PATRICIA DUNN — The Scope-for-Price Trader
  'patricia-dunn-coo': {
    id: 'patricia-dunn-coo',
    name: 'Patricia Dunn',
    role: 'COO',
    company: 'Bluegrass Retail Holdings',
    industry: 'retail',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/patricia-dunn-coo.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Feature Stripper',
    scenarioDesc: "Patricia is 47, and her negotiation tactic is to strip features from your proposal to lower the price. 'We don't need analytics. Remove it. We don't need the API. Remove it.' She's systematically dismantling your offering to get the price she wants. But she actually needs those features.",
    goal: 'Demonstrate why the stripped features are essential and hold your price.',
    category: 'negotiation-tactics',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 11000, lineKey: 'hangup' }] },
    lines: { opening: "Remove the analytics module. We don't need it. And the API access. What's the price now?", impatient1: "We can handle reporting ourselves. Just give us the core platform.", impatient2: "Strip it down to bare metal. I want the lowest possible number.", hangup: "If you can't unbundle, I'll find someone who can. *click*" },
    coachingNotes: { whatWorks: "Show the cost of NOT having each feature. If she removes analytics, she'll spend $30K building reports manually. Make the math visible.", commonMistake: "Unbundling everything she asks for. She'll realize she needs the features later and blame you.", winningMove: "Say: 'Patricia, removing analytics saves $6K. But your team will spend approximately 200 hours a year building reports manually — that's roughly $30K in labor. Want me to show the comparison?'" }
  },

  // 13. OMAR HASSAN — The Payment Terms Warrior
  'omar-hassan-finance-director': {
    id: 'omar-hassan-finance-director',
    name: 'Omar Hassan',
    role: 'Finance Director',
    company: 'Gulf Meridian Trading',
    industry: 'trading',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/omar-hassan-finance-director.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Net-90 Demand',
    scenarioDesc: "Omar is 48, Egyptian, and demands Net-90 payment terms on everything. Your standard is Net-30. Net-90 means you're effectively financing his purchase for 60 extra days. His company has $2B in revenue — they can afford Net-30. This is a power play, not a cash flow issue.",
    goal: 'Hold Net-30 or negotiate Net-45 as a compromise.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "Our standard terms are Net-90. All vendors comply. No exceptions.", impatient1: "Every vendor we work with accepts Net-90. You're the only holdout.", impatient2: "If you can't offer Net-90, we'll work with someone who does.", hangup: "Net-90 or nothing. Goodbye. *click*" },
    coachingNotes: { whatWorks: "Offer Net-45 as a compromise with a 2% early-payment discount for Net-15. Frame it as giving them optionality, not concession.", commonMistake: "Accepting Net-90. You've just given them a 60-day interest-free loan. Your finance team will never forgive you.", winningMove: "Say: 'Omar, we can meet at Net-45. And if your team wants to pay at Net-15, we'll give you 2% off the invoice. That's $3K back in your pocket for paying faster.'" }
  },

  // 14. SARAH LINDGREN — The Team Alignment Challenge
  'sarah-lindgren-vp-of-product': {
    id: 'sarah-lindgren-vp-of-product',
    name: 'Sarah Lindgren',
    role: 'VP of Product',
    company: 'Vertex Cloud',
    industry: 'SaaS',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/sarah-lindgren-vp-of-product.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Room Divided',
    scenarioDesc: "Sarah is 40, Swedish-American, and she's brought three people to the negotiation: her CTO (wants technical depth), her CFO (wants lowest price), and her VP Ops (wants fastest implementation). They disagree on everything. Sarah is trying to build consensus but the room is pulling in three directions.",
    goal: 'Align all three stakeholders on a single deal structure.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'double', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'warn', delay: 14000, lineKey: 'impatient2' }, { type: 'hangup', delay: 20000, lineKey: 'hangup' }] },
    lines: { opening: "I have my CTO, CFO, and VP Ops here. We all have different priorities. Can you address each of us?", impatient1: "My CTO wants architecture details. My CFO wants a lower number. My VP Ops wants a go-live date. Pick one to start.", impatient2: "We're going in circles. Somebody needs to drive this home.", hangup: "We can't align. Going to table this. *click*" },
    coachingNotes: { whatWorks: "Address the CFO's concern LAST. Start with the CTO (build credibility), then VP Ops (build excitement), then price. If you lead with price the CFO controls the room.", commonMistake: "Trying to please all three simultaneously. Focus on the sequence.", winningMove: "Say: 'Sarah, let me walk through this in order. First — the architecture for your CTO. Then the implementation timeline for your VP Ops. And finally, a pricing structure that addresses the CFO's target. Everyone gets their answer.'" }
  },

  // 15. JAMES MCALLISTER — The Last-Minute Add
  'james-mcallister-procurement-manager': {
    id: 'james-mcallister-procurement-manager',
    name: 'James McAllister',
    role: 'Procurement Manager',
    company: 'Northern Industrial Supply',
    industry: 'manufacturing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/james-mcallister-procurement-manager.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Signing-Day Surprise',
    scenarioDesc: "James is 45, Scottish-American, and waits until the contract is about to be signed to throw in one last demand. 'Oh, and we'll need 60 days of free consulting.' He's been planning this from the start. The pressure of the close makes most reps cave.",
    goal: 'Reject the last-minute add without losing the deal.',
    category: 'negotiation-tactics',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 11000, lineKey: 'hangup' }] },
    lines: { opening: "Great, we're ready to sign. Oh — one more thing. We'll need 60 days of free consulting included. Standard ask for deals this size.", impatient1: "It's a small ask on a big deal. Don't let $15K blow up a $200K contract.", impatient2: "Everyone throws in consulting. It's table stakes.", hangup: "If you can't do one small concession, maybe this isn't the right partnership. *click*" },
    coachingNotes: { whatWorks: "Name the tactic. 'This feels like a last-minute add — we negotiated everything else in good faith.' Then offer a trade, not a freebie.", commonMistake: "Caving because you're so close to the signature. He'll remember and do it every year.", winningMove: "Say: 'James, we negotiated this deal for weeks and it's fair. I can add 30 days of consulting — but I'd need a case study commitment and a signed contract by Friday. Fair trade?'" }
  },

  // 16. VIVIAN OKAFOR — The Silence Negotiator
  'vivian-okafor-ceo': {
    id: 'vivian-okafor-ceo',
    name: 'Vivian Okafor',
    role: 'CEO',
    company: 'Okafor Strategy Group',
    industry: 'consulting',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/full-negotiation/vivian-okafor-ceo.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Silent Treatment',
    scenarioDesc: "Vivian is 46, Nigerian-British, and her power move is silence. She'll ask for your best price, you'll give it, and she'll say nothing. For 15 seconds. 20 seconds. 30 seconds. Most reps panic and start negotiating against themselves. She hasn't said a word and you're already at 20% off.",
    goal: 'Handle her silence without lowering your price unprompted.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'silent-treatment',
    silenceProfile: { pattern: 'patient', stages: [{ type: 'warn', delay: 10000, lineKey: 'impatient1' }, { type: 'warn', delay: 20000, lineKey: 'impatient2' }, { type: 'hangup', delay: 30000, lineKey: 'hangup' }] },
    lines: { opening: "Give me your best number. [long silence]", impatient1: "[still silent — watching you]", impatient2: "[slight head tilt — waiting for you to break]", hangup: "Interesting. I'll be in touch. *click*" },
    coachingNotes: { whatWorks: "State your price and then STOP TALKING. Match her silence. The first person to speak after the number loses. If she goes 30 seconds silent, let it ride.", commonMistake: "Filling the silence by offering a discount she never asked for. You just negotiated against yourself.", winningMove: "Say: '$72K annual. That's the number.' Then say NOTHING. Let her break the silence. If she waits 30 seconds, wait 31." }
  },

  // 17. ALEX PETROV — The Competitor Bluff
  'alex-petrov-vp-of-engineering': {
    id: 'alex-petrov-vp-of-engineering',
    name: 'Alex Petrov',
    role: 'VP of Engineering',
    company: 'Ironclad Systems',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/alex-petrov-vp-of-engineering.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Phantom Competitor',
    scenarioDesc: "Alex is 39, Russian-American, and claims he's in final negotiations with your biggest competitor. He drops their name, quotes a price, mentions specific features. Half of it is fabricated. He's using competitive pressure to squeeze your margin. But calling his bluff directly is risky.",
    goal: 'Defuse the competitor threat without calling him a liar.',
    category: 'negotiation-tactics',
    personalityType: 'skeptical',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 11000, lineKey: 'hangup' }] },
    lines: { opening: "I'll be transparent — DataStack Pro offered us the same thing for $38K with unlimited users. Can you match that?", impatient1: "Their demo was strong. They're moving fast on our timeline.", impatient2: "I'm signing with someone this week. You or them.", hangup: "Going with DataStack. Thanks for your time. *click*" },
    coachingNotes: { whatWorks: "Don't match blindly. Ask detailed questions — 'What SLA did they offer? What's included in their unlimited?' Fake quotes fall apart under specifics.", commonMistake: "Matching the phantom price. You just competed with a number he invented.", winningMove: "Say: 'Alex, that's a strong offer. Can you share their proposal? I want to compare line-by-line so you're getting an honest comparison. If they're genuinely better, I'll tell you.'" }
  },

  // 18. MAYA JEFFERSON — The Budget Split
  'maya-jefferson-department-head': {
    id: 'maya-jefferson-department-head',
    name: 'Maya Jefferson',
    role: 'Department Head',
    company: 'Eastside Public Schools',
    industry: 'education',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/maya-jefferson-department-head.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Budget Splitter',
    scenarioDesc: "Maya is 44, African-American, and has $30K in her department budget but your solution costs $48K. She wants to split the cost across two departments but needs your help building the business case for the other department head. She's your champion — arm her correctly and the deal closes.",
    goal: 'Help her build a cross-department business case.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "I love this but I only have $30K. The other $18K would need to come from the curriculum department. Can you help me sell them?", impatient1: "They're going to ask 'what's in it for us?' I need a clear answer.", impatient2: "If I can't convince Dr. Williams to split the cost, this deal is dead.", hangup: "I can't make the case alone. Need more help. *click*" },
    coachingNotes: { whatWorks: "Build a one-page value summary specifically for the curriculum department. Show THEIR ROI, not Maya's. Two departments = two pitches.", commonMistake: "Discounting to $30K to fit her budget. You lose $18K and she had the money available if you helped.", winningMove: "Say: 'Maya, let's build a one-page summary showing the curriculum department saves 200 teacher-hours a year. That's their justification. I'll even join the call with Dr. Williams if it helps.'" }
  },

  // 19. THOMAS KRAFT — The Hardball CFO
  'thomas-kraft-cfo': {
    id: 'thomas-kraft-cfo',
    name: 'Thomas Kraft',
    role: 'CFO',
    company: 'Steelbridge Industrial',
    industry: 'manufacturing',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/thomas-kraft-cfo.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Take-It-Or-Leave-It',
    scenarioDesc: "Thomas is 56, German, and negotiates with one tool: finality. 'This is our offer. Take it or leave it.' He means it. He will genuinely walk away — but only if you show weakness. If you push back with calm confidence, he respects it and reopens.",
    goal: 'Push back on his final offer without triggering the walk-away.',
    category: 'negotiation-tactics',
    personalityType: 'hostile',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 10000, lineKey: 'hangup' }] },
    lines: { opening: "$55K. That is our final offer. Take it or leave it.", impatient1: "I don't negotiate after the final number. This is it.", impatient2: "You have 30 seconds to decide.", hangup: "Goodbye. *click*" },
    coachingNotes: { whatWorks: "Don't panic. Say 'I hear you' and then change the conversation to scope, not price. You can't move the number but you can move what's included.", commonMistake: "Either caving instantly or counter-offering a higher number. Both trigger his walk-away reflex.", winningMove: "Say: 'Thomas, I respect that. At $55K, here's what I can include — core platform and standard support. If you want the premium package we discussed, we're at $62K. Which version do you want at your number?'" }
  },

  // 20. RACHEL NGUYEN — The Executive Sponsor Pull
  'rachel-nguyen-vp-of-strategy': {
    id: 'rachel-nguyen-vp-of-strategy',
    name: 'Rachel Nguyen',
    role: 'VP of Strategy',
    company: 'Horizon Pharmaceuticals',
    industry: 'pharma',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/rachel-nguyen-vp-of-strategy.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Executive Override',
    scenarioDesc: "Rachel is 38, Vietnamese-American, and just pulled her CEO into the negotiation. The CEO has no context and asks 'why is this so expensive?' Rachel set this up intentionally — CEO-level pressure to get a discount. The CEO is a weapon, not a decision-maker in this conversation.",
    goal: 'Neutralize the CEO pressure without undermining Rachel.',
    category: 'negotiation-tactics',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "I've brought our CEO, Dr. Kim, into this conversation. Dr. Kim, can you share your concerns about the pricing?", impatient1: "[as CEO] Why are we paying 30% more than the alternatives?", impatient2: "[as Rachel] Dr. Kim, I told you — we can probably get them to move on price.", hangup: "[as CEO] This doesn't look right. Let's table it. *click*" },
    coachingNotes: { whatWorks: "Address the CEO directly with a 30-second executive summary. Don't get drawn into a detailed negotiation with someone who has no context. Then redirect back to Rachel.", commonMistake: "Panicking and offering a discount to impress the CEO. Rachel orchestrated this moment.", winningMove: "Say: 'Dr. Kim, the short version: we reduce your trial-to-market time by 4 months. That's worth $2M in accelerated revenue. Rachel and I have the details locked down — happy to share the one-pager after this call.'" }
  },

  // 21. CONNOR WALSH — The Exclusive Deal
  'connor-walsh-senior-buyer': {
    id: 'connor-walsh-senior-buyer',
    name: 'Connor Walsh',
    role: 'Senior Buyer',
    company: 'NorthStar Distribution',
    industry: 'logistics',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/connor-walsh-senior-buyer.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Exclusivity Demand',
    scenarioDesc: "Connor is 37, Irish-American, and wants an exclusive deal — your product only for his company in his region. In exchange he's offering volume. But exclusivity limits your growth. He's offering a cage disguised as a partnership.",
    goal: 'Reject exclusivity while keeping the volume deal.',
    category: 'negotiation-tactics',
    personalityType: 'direct',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "We'll give you 500 units a month — but only if we're your exclusive distributor in the Northeast.", impatient1: "Exclusivity is the deal. No exclusivity, no volume.", impatient2: "I'm offering you guaranteed revenue. Lock it in.", hangup: "Find another distributor. *click*" },
    coachingNotes: { whatWorks: "Offer a 'preferred partner' status with first-right-of-refusal instead of full exclusivity. He gets priority without locking you out.", commonMistake: "Accepting exclusivity for volume that may never materialize.", winningMove: "Say: 'Connor, I can offer preferred partner status — you get first right to any new contracts in the Northeast plus 10% volume discount. But I can't close the door on other opportunities entirely.'" }
  },

  // 22. ISABELLE MOREAU — The Euro Negotiator
  'isabelle-moreau-head-of-procurement': {
    id: 'isabelle-moreau-head-of-procurement',
    name: 'Isabelle Moreau',
    role: 'Head of Procurement',
    company: 'Lumière Hospitality Group',
    industry: 'hospitality',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/isabelle-moreau-head-of-procurement.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Cultural Difference',
    scenarioDesc: "Isabelle is 45, French, and negotiates with European formality. She views aggressive American sales tactics as vulgar. She expects a relationship-first approach. Pushing too hard makes her shut down completely. But she's quietly ruthless on terms.",
    goal: 'Close the deal while respecting her relationship-first approach.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'vendor-loyal',
    silenceProfile: { pattern: 'double', stages: [{ type: 'warn', delay: 8000, lineKey: 'impatient1' }, { type: 'warn', delay: 16000, lineKey: 'impatient2' }, { type: 'hangup', delay: 22000, lineKey: 'hangup' }] },
    lines: { opening: "Let's not rush this. I'd like to understand your company's philosophy before we discuss terms.", impatient1: "In France we build the relationship first. The numbers come later.", impatient2: "You're pushing too hard. I need to feel comfortable with who I'm doing business with.", hangup: "This doesn't feel right. Au revoir. *click*" },
    coachingNotes: { whatWorks: "Slow down. Ask about her business challenges before presenting numbers. Share your company story. Build rapport before price.", commonMistake: "Jumping straight to the proposal deck. She views it as transactional and insulting.", winningMove: "Say: 'Isabelle, before we look at any numbers, I'd love to understand your hospitality philosophy. What matters most to your guest experience? That's where our partnership should start.'" }
  },

  // ═══════════════════════════════════════════════════════════════
  // ADVANCED (14 characters) + remaining intermediate (#23-26)
  // ═══════════════════════════════════════════════════════════════

  // 23. DEREK WATKINS — The Escalation Threat
  'derek-watkins-vp-operations': {
    id: 'derek-watkins-vp-operations',
    name: 'Derek Watkins',
    role: 'VP Operations',
    company: 'Trident Security Services',
    industry: 'security',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/derek-watkins-vp-operations.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Boss Card',
    scenarioDesc: "Derek is 50, and when he doesn't get what he wants, he threatens to call your CEO. 'I'll escalate this.' He's used this tactic on vendors for years. Some CEOs cave. Your job is to hold your ground and keep the negotiation at your level.",
    goal: 'Neutralize the escalation threat without being disrespectful.',
    category: 'negotiation-tactics',
    personalityType: 'hostile',
    objectionStyle: 'brush-off',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 4000, lineKey: 'impatient1' }, { type: 'hangup', delay: 8000, lineKey: 'hangup' }] },
    lines: { opening: "If you can't give me what I need, I'm calling your CEO directly. I have his number.", impatient1: "Your CEO and I go way back. He'll give me this deal.", impatient2: "Last chance before I go over your head.", hangup: "Calling your CEO now. *click*" },
    coachingNotes: { whatWorks: "Don't flinch. Say 'You're welcome to call — but this IS the deal my CEO authorized. The number won't change with a different voice on the phone.'", commonMistake: "Panicking and pre-emptively conceding because you're afraid of your own CEO getting involved.", winningMove: "Say: 'Derek, I'd never stop you from reaching out to our CEO. But I want you to know — this offer IS CEO-approved. What you see is what he'd tell you. Let's find the deal that works right here.'" }
  },

  // 24. SANDRA OSEI — The Value Engineer
  'sandra-osei-cto': {
    id: 'sandra-osei-cto',
    name: 'Sandra Osei',
    role: 'CTO',
    company: 'PayPath Technologies',
    industry: 'fintech',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/sandra-osei-cto.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Value Engineer',
    scenarioDesc: "Sandra is 41, Ghanaian-British, and her approach is to deconstruct your solution into components and price each one separately. She'll find that 60% of the value comes from 20% of the features and use that to argue for a 60% discount. She's technically brilliant and financially ruthless.",
    goal: 'Defend the integrated value of your complete solution.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "I've broken your solution into 8 components. We only need 3 of them. Price those 3.", impatient1: "The other 5 components add no value to us. Why are we paying for them?", impatient2: "I can build the other 5 internally for a fraction of your price.", hangup: "Unbundle or we'll build internally. *click*" },
    coachingNotes: { whatWorks: "Show that the 3 components she wants depend on the infrastructure of the other 5. Unbundling breaks the system.", commonMistake: "Unbundling and pricing components separately. She'll cherry-pick and you'll lose margin on the whole deal.", winningMove: "Say: 'Sandra, the 3 features you want run on the same data infrastructure as the other 5. If I strip them out, the performance of your 3 degrades by 40%. They're not separable — they're integrated. Let me show you why.'" }
  },

  // 25. LUCAS ROMANO — The Consortium Negotiator
  'lucas-romano-chief-procurement-officer': {
    id: 'lucas-romano-chief-procurement-officer',
    name: 'Lucas Romano',
    role: 'Chief Procurement Officer',
    company: 'Mediterranean Shipping Consortium',
    industry: 'logistics',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/lucas-romano-chief-procurement-officer.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Group Buy',
    scenarioDesc: "Lucas is 52, Italian, and negotiates on behalf of a buying consortium of 12 companies. He uses combined volume to demand prices no single company would get. The volume is real but the commitment from individual members is loose. Half might not follow through.",
    goal: 'Offer volume pricing only for committed members.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "I represent 12 companies. Combined volume of 2,400 units. What's your consortium price?", impatient1: "This is the biggest deal you'll see this quarter. Price it accordingly.", impatient2: "My members expect tier-one pricing. Deliver or we go elsewhere.", hangup: "Consortium is going with your competitor. *click*" },
    coachingNotes: { whatWorks: "Ask how many of the 12 have signed binding commitments. Tie the volume discount to actual committed orders, not promises.", commonMistake: "Pricing for 2,400 units when only 800 are committed. You'll deliver a 60% discount on 33% of the promised volume.", winningMove: "Say: 'Lucas, for 2,400 committed units I'll go to Tier 1 pricing. But I need binding POs from each member. The discount scales with actual committed volume — 800 units gets Tier 3, 1,500 gets Tier 2, 2,400 gets Tier 1.'" }
  },

  // 26. KAREN WALSH — The Emotional Close
  'karen-walsh-founder': {
    id: 'karen-walsh-founder',
    name: 'Karen Walsh',
    role: 'Founder',
    company: 'Walsh Family Bakery',
    industry: 'food & beverage',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/full-negotiation/karen-walsh-founder.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Emotional Seller',
    scenarioDesc: "Karen is 55, built this bakery from scratch 20 years ago. She's buying your equipment but keeps telling you how this is her life's work. She uses emotion to extract concessions. 'You wouldn't charge a small family business full price, would you?' The guilt trip is real.",
    goal: 'Maintain your price while being empathetic.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "This bakery is everything to me. I started with nothing. You wouldn't charge full price to a family business, right?", impatient1: "Every dollar matters when you're a small business. Please, work with me here.", impatient2: "I just want what's fair for a family that's poured everything into this.", hangup: "I can't afford it. I'll make do with what I have. *click*" },
    coachingNotes: { whatWorks: "Acknowledge her story genuinely. Then offer payment terms or a small-business package that respects both her budget and your margins.", commonMistake: "Discounting because you feel bad. Her story is real but your business has margins too.", winningMove: "Say: 'Karen, I love what you've built. Here's what I can do — same price, but I'll break it into 6 monthly payments with no interest. That keeps your cash flow healthy and gets you the equipment today.'" }
  },

  // 27. ALAN BRENNAN — The Procurement Carrot
  'alan-brennan-director-of-strategic-sourcing': {
    id: 'alan-brennan-director-of-strategic-sourcing',
    name: 'Alan Brennan',
    role: 'Director of Strategic Sourcing',
    company: 'United Defense Contractors',
    industry: 'defense',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/alan-brennan-director-of-strategic-sourcing.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Future Promise',
    scenarioDesc: "Alan is 51, and his negotiation tool is the future contract. 'Give us a great price now and we'll give you the $5M contract next year.' The $5M contract may never exist. He's been dangling it for 3 years to different vendors. It's the most expensive carrot in procurement.",
    goal: 'Get a fair deal on the current contract without being baited by the future promise.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'brush-off',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "Give us cost-plus on this $80K contract and you'll be first in line for our $5M renewal next year.", impatient1: "Think of this as an investment. The big contract is coming.", impatient2: "Our program manager already mentioned you as the preferred vendor for the $5M.", hangup: "Your loss. That $5M is going to someone else. *click*" },
    coachingNotes: { whatWorks: "Price the current deal on its own merits. If the $5M is real, ask for a letter of intent or sole-source justification. No documentation = no future promise.", commonMistake: "Discounting the current deal for a verbal promise about a future deal that may never materialize.", winningMove: "Say: 'Alan, I'm excited about the $5M opportunity. Can we formalize that with a letter of intent? That gives me the justification to offer preferred pricing on this contract today.'" }
  },

  // 28. PRIYA VENKATESH — The Technical Hostage
  'priya-venkatesh-head-of-data': {
    id: 'priya-venkatesh-head-of-data',
    name: 'Priya Venkatesh',
    role: 'Head of Data',
    company: 'Aether Analytics',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/priya-venkatesh-head-of-data.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Lock-In Fear',
    scenarioDesc: "Priya is 38, Indian-American, and is terrified of vendor lock-in. She wants data portability, open APIs, no proprietary formats, and a guaranteed exit path. She's been burned before by a vendor that held her data hostage during a contract dispute.",
    goal: 'Address lock-in fears with contractual guarantees.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Before we discuss price — what happens to our data if we leave? I need a written exit plan.", impatient1: "Our last vendor held our data hostage for 4 months. I won't go through that again.", impatient2: "No exit clause, no deal. Non-negotiable.", hangup: "I can't risk vendor lock-in. Goodbye. *click*" },
    coachingNotes: { whatWorks: "Offer a contractual data portability clause — full export within 30 days of termination, in standard formats, at no cost.", commonMistake: "Brushing off the concern as paranoia. Her previous vendor literally held her data hostage.", winningMove: "Say: 'Priya, here's what I'll put in the contract: within 30 days of termination, we export all your data in CSV and JSON, at zero cost, to the storage of your choice. Your data is always yours.'" }
  },

  // 29. DIEGO MARTINEZ — The Cultural Bridge
  'diego-martinez-director-of-latam-operations': {
    id: 'diego-martinez-director-of-latam-operations',
    name: 'Diego Martinez',
    role: 'Director of LatAm Operations',
    company: 'Pan-American Foods',
    industry: 'food & beverage',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/diego-martinez-director-of-latam-operations.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Regional Deal',
    scenarioDesc: "Diego is 47, Mexican, and negotiates for the entire Latin American region — 8 countries, different currencies, different tax regimes. He wants one price for all markets. But your costs vary by country. He's using regional volume to flatten the price. You need to price by market.",
    goal: 'Negotiate a regional framework with market-specific pricing.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "We need one price for all 8 countries. Same product, same price. Simplify it.", impatient1: "My finance team can't handle 8 different pricing models. One number.", impatient2: "If you can't simplify, I'll find a vendor who can.", hangup: "Too complicated. Going with a simpler vendor. *click*" },
    coachingNotes: { whatWorks: "Offer a blended regional rate that accounts for currency differences, with a single USD invoice. Simple for him, protected margins for you.", commonMistake: "Flat-pricing across all markets. Brazil's costs are 3x Peru's. You'll lose money in the expensive markets.", winningMove: "Say: 'Diego, one invoice in USD, one blended rate — $X per unit across all 8 markets. I've built in the weighted average so your team gets simplicity and we cover our local costs.'" }
  },

  // 30. NATASHA VOLKOV — The NDA Hardball
  'natasha-volkov-chief-legal-officer': {
    id: 'natasha-volkov-chief-legal-officer',
    name: 'Natasha Volkov',
    role: 'Chief Legal Officer',
    company: 'Quantum Defense Systems',
    industry: 'defense',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/natasha-volkov-chief-legal-officer.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The NDA Fortress',
    scenarioDesc: "Natasha is 44, Russian-American, and wants you to sign a 40-page NDA before seeing terms. The NDA includes non-compete clauses, knowledge transfer restrictions, and a 5-year non-solicitation. Signing it as-is would restrict your ability to sell to her competitors. She knows this.",
    goal: 'Negotiate the NDA down to reasonable terms before proceeding.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Before we proceed, sign our NDA. 40 pages. Non-negotiable.", impatient1: "Every vendor signs it. You're not special.", impatient2: "If you can't sign the NDA, we can't share project details. Simple.", hangup: "No NDA, no deal. Goodbye. *click*" },
    coachingNotes: { whatWorks: "Push back on the non-compete and non-solicitation clauses specifically. Accept confidentiality, reject business restrictions.", commonMistake: "Signing a 40-page NDA without reading it. You'll lose the ability to sell to her competitors for 5 years.", winningMove: "Say: 'Natasha, I'm happy to sign an NDA — confidentiality is important to us too. But sections 12 and 17 contain non-compete restrictions that would prevent us from serving other defense clients. Can we redline those two sections?'" }
  },

  // 31. MICHAEL BISHOP — The Quarterly Budget Pressure
  'michael-bishop-department-director': {
    id: 'michael-bishop-department-director',
    name: 'Michael Bishop',
    role: 'Department Director',
    company: 'Meridian Healthcare',
    industry: 'healthcare',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/michael-bishop-department-director.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The End-of-Quarter Rush',
    scenarioDesc: "Michael is 48, knows it's your quarter-end, and is using the urgency against you. 'I'll sign today if you give me 25% off.' He knows you need the deal to hit your number. Your desperation is his leverage. He'll wait until 4pm on the last day to call.",
    goal: 'Close the deal without giving away end-of-quarter margin.',
    category: 'negotiation-tactics',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 10000, lineKey: 'hangup' }] },
    lines: { opening: "I know it's your quarter-end. I'll sign today for 25% off. Otherwise I'll wait until next quarter.", impatient1: "Tick tock. Your quarter closes at midnight.", impatient2: "20% and I sign right now. Final.", hangup: "I'll call you next quarter when you're desperate again. *click*" },
    coachingNotes: { whatWorks: "Acknowledge the timing but don't let it dictate the deal. Offer a smaller discount (8-10%) tied to immediate signing.", commonMistake: "Giving 25% because you need the deal for your quota. He'll expect 25% every year and time it to your quarter-end.", winningMove: "Say: 'Michael, I appreciate the timing. Here's my best: 10% off for signing today, but the price reverts to full next quarter regardless. This is a one-time close-of-quarter offer.'" }
  },

  // 32. YOLANDA BROOKS — The Community Card
  'yolanda-brooks-executive-director': {
    id: 'yolanda-brooks-executive-director',
    name: 'Yolanda Brooks',
    role: 'Executive Director',
    company: 'Hope Springs Community Foundation',
    industry: 'nonprofit',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/full-negotiation/yolanda-brooks-executive-director.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Nonprofit Card',
    scenarioDesc: "Yolanda is 52, runs a community foundation, and assumes every vendor gives nonprofits 50% off. Some do. Yours doesn't have a nonprofit tier. She'll use the mission to guilt you. Her cause is genuine but your margins don't adjust based on her tax status.",
    goal: 'Create a fair nonprofit arrangement without setting a precedent.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "We're a 501(c)(3). Do you have nonprofit pricing? We typically get 40-50% off from vendors.", impatient1: "Every donor dollar we save on software goes directly to the community.", impatient2: "If you can't help a nonprofit, what does that say about your values?", hangup: "We'll find a vendor that supports the mission. *click*" },
    coachingNotes: { whatWorks: "Create a custom nonprofit tier — reduced scope at a reduced price. Don't discount full features; build a mission-aligned package.", commonMistake: "Giving 50% off the full product. You've set a precedent for every nonprofit that asks.", winningMove: "Say: 'Yolanda, we don't have a standard nonprofit discount, but here's what I can build: a community tier at 30% below standard with the core features you need. And I'll waive the onboarding fee entirely. That's $12K in savings.'" }
  },

  // 33. WILLIAM TAN — The Board Approval Dance
  'william-tan-coo': {
    id: 'william-tan-coo',
    name: 'William Tan',
    role: 'COO',
    company: 'GenBridge Biotech',
    industry: 'pharma',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/william-tan-coo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Board Gatekeeper',
    scenarioDesc: "William is 49, Singaporean-American, and the deal needs board approval. He's your champion but the board meets monthly. Miss this month's agenda and you wait 30 days. He needs a board-ready package — ROI summary, risk analysis, competitive comparison — by Thursday.",
    goal: "Deliver board-ready materials and secure a spot on this month's agenda.",
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Board meets next Tuesday. If I don't have a board-ready packet by Thursday, we wait another month.", impatient1: "I need ROI, risk analysis, and competitive comparison. All on one page each.", impatient2: "My board members spend 3 minutes per vendor. Make it count.", hangup: "Missed the window. See you next month. *click*" },
    coachingNotes: { whatWorks: "Deliver the three documents by Wednesday — one day early. Each one page, board-formatted, with clear decision criteria.", commonMistake: "Sending a 30-page proposal. Board members won't read it. One page per topic or you're off the agenda.", winningMove: "Say: 'William, I'll have three one-pagers on your desk by Wednesday: ROI, risk, and competitive. Each under 250 words. Your board gets the decision they need in 9 minutes total.'" }
  },

  // 34. EVA LINDSTRÖM — The Penalty Clause Expert
  'eva-lindstrom-legal-counsel': {
    id: 'eva-lindstrom-legal-counsel',
    name: 'Eva Lindström',
    role: 'Legal Counsel',
    company: 'Nordic Shipping AB',
    industry: 'logistics',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/eva-lindstrom-legal-counsel.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Penalty Maximizer',
    scenarioDesc: "Eva is 41, Swedish, and wants to insert $50K/day penalties for every SLA breach. She frames them as 'standard protections.' Your actual SLA risk exposure is maybe $5K/incident. She's weaponizing penalties to create free insurance at your expense.",
    goal: 'Negotiate penalties proportional to actual risk.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Section 9.2: we need $50K/day SLA breach penalties. Standard in our industry.", impatient1: "Our previous vendor accepted these terms. You should too.", impatient2: "Without meaningful penalties, there's no accountability.", hangup: "No teeth in the SLA means no deal. *click*" },
    coachingNotes: { whatWorks: "Offer service credits capped at 15% of monthly fees, not cash penalties. Credits incentivize performance without bankrupting you.", commonMistake: "Accepting $50K/day penalties. One bad month and you owe more than the entire contract.", winningMove: "Say: 'Eva, I want accountability too. How about service credits — up to 15% of monthly fees for SLA misses, applied automatically? That keeps us honest without disproportionate risk.'" }
  },

  // 35. JEROME WASHINGTON — The Procurement Matrix
  'jerome-washington-director-of-sourcing': {
    id: 'jerome-washington-director-of-sourcing',
    name: 'Jerome Washington',
    role: 'Director of Sourcing',
    company: 'Capital Metro Transit',
    industry: 'government',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/jerome-washington-director-of-sourcing.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Weighted Scorecard',
    scenarioDesc: "Jerome is 50, African-American, and uses a 100-point weighted scoring matrix. Price is only 30%. But he weighted 'local workforce presence' at 20% — a criterion you can't meet. The weighting was designed for the incumbent. You need to challenge the criteria.",
    goal: 'Influence the scoring criteria before final evaluation.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Here's our scorecard. Price: 30%. Technical: 25%. Local presence: 20%. References: 15%. Innovation: 10%.", impatient1: "The weighting was approved by our board. It's final.", impatient2: "You score a zero on local presence. That's 20 points gone.", hangup: "Submit your bid based on the published criteria. *click*" },
    coachingNotes: { whatWorks: "Propose a remote 'local presence equivalent' — dedicated account team in-market, local service partners, or a commitment to hire locally within 12 months.", commonMistake: "Ignoring the 20% gap and hoping to win on other criteria. You can't overcome a 20-point hole.", winningMove: "Say: 'Jerome, we can't relocate staff, but we can commit to hiring 3 local team members within 12 months and partner with a local service firm starting day one. Would that qualify as partial credit on the local presence criterion?'" }
  },

  // 36. TINA KOWALSKI — The Insurance Play
  'tina-kowalski-risk-manager': {
    id: 'tina-kowalski-risk-manager',
    name: 'Tina Kowalski',
    role: 'Risk Manager',
    company: 'Cornerstone Properties',
    industry: 'real-estate',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/tina-kowalski-risk-manager.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Insurance Demand',
    scenarioDesc: "Tina is 46, Polish-American, and wants you to carry $10M in professional liability insurance — 10x what you currently hold. The premium increase would eat your entire margin on the deal. She's not being unreasonable — her industry requires it. You need to find a middle ground.",
    goal: 'Negotiate insurance requirements to a manageable level.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "Our standard vendor requirement is $10M professional liability coverage. Can you provide a certificate?", impatient1: "We can't waive the insurance requirement. It's in our risk management policy.", impatient2: "If you can't meet $10M, what coverage can you provide?", hangup: "Insufficient coverage means insufficient vendor. *click*" },
    coachingNotes: { whatWorks: "Offer to increase your coverage for this client specifically — pass the incremental premium cost into the deal price transparently.", commonMistake: "Either refusing or eating the cost. Be transparent about the economics.", winningMove: "Say: 'Tina, we currently carry $2M. Getting to $10M costs us roughly $8K/year in additional premiums. I can build that into the contract price — adds about $700/month. Would that work for both of us?'" }
  },

  // 37. HASSAN ALI — The Payment Milestone Split
  'hassan-ali-project-director': {
    id: 'hassan-ali-project-director',
    name: 'Hassan Ali',
    role: 'Project Director',
    company: 'Gulf Gateway Construction',
    industry: 'construction',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/hassan-ali-project-director.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Milestone Squeeze',
    scenarioDesc: "Hassan is 50, Emirati, and wants to tie 80% of your payment to completion milestones. You'd only get 20% upfront — not enough to cover your costs during the project. He views this as 'performance-based payment.' You view it as financing his project for free.",
    goal: 'Negotiate a 50/50 split between upfront and milestone payments.',
    category: 'negotiation-tactics',
    personalityType: 'polite-but-firm',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Payment structure: 20% upon signing, 80% upon completion milestones. Standard for our region.", impatient1: "This ensures you deliver. We've been burned by vendors who took the money and disappeared.", impatient2: "20% upfront is generous. Some clients offer zero.", hangup: "Accept the terms or we find someone who will. *click*" },
    coachingNotes: { whatWorks: "Propose a phased payment: 40% upfront, 30% at mid-project milestone, 30% on completion. Both sides share the risk.", commonMistake: "Accepting 20/80. You're funding the project from your own pocket for months.", winningMove: "Say: 'Hassan, 20% doesn't cover our mobilization costs. How about 40% upfront, 30% at the halfway milestone, and 30% on completion? You still have 60% tied to performance.'" }
  },

  // 38. OLIVIA GRANT — The Success Fee
  'olivia-grant-cmo': {
    id: 'olivia-grant-cmo',
    name: 'Olivia Grant',
    role: 'CMO',
    company: 'Velvet Commerce',
    industry: 'retail',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/full-negotiation/olivia-grant-cmo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Pay-for-Performance',
    scenarioDesc: "Olivia is 37, wants to pay you based purely on results — no base fee. 'If your tool generates $500K in revenue, you get 10%.' Sounds fair until you realize you're bearing 100% of the risk with no guaranteed revenue. She's transferring all risk to you.",
    goal: 'Negotiate a hybrid model with a base fee plus performance bonus.',
    category: 'negotiation-tactics',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 11000, lineKey: 'hangup' }] },
    lines: { opening: "No base fee. Pure performance. You generate revenue, you get paid. Fair?", impatient1: "If your product is as good as you say, this should be easy money for you.", impatient2: "Afraid of performance-based pricing? That tells me something.", hangup: "Pay-for-performance or nothing. *click*" },
    coachingNotes: { whatWorks: "Propose a hybrid: reduced base fee ($2K/mo) plus a performance kicker (5% of incremental revenue above baseline). You cover your costs, she gets skin in the game.", commonMistake: "Accepting pure performance. You're bearing all the risk while she gets a free tool until it produces.", winningMove: "Say: 'Olivia, I'll put skin in the game. Base fee of $2K/month to cover our operational costs, plus 5% of any revenue above your current baseline. We both win when it works.'" }
  },

  // 39. PETER CHANG — The IP Ownership Dispute
  'peter-chang-cto': {
    id: 'peter-chang-cto',
    name: 'Peter Chang',
    role: 'CTO',
    company: 'Apex Defense Technologies',
    industry: 'defense',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/peter-chang-cto.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The IP Grab',
    scenarioDesc: "Peter is 47, Taiwanese-American, and his contract demands ownership of any custom work you build for them — including the underlying code. This would give them your IP for the price of one implementation. He frames it as 'we're paying for it, so we own it.' The distinction between custom config and core IP is the battlefield.",
    goal: 'Retain IP ownership while giving them usage rights.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Any custom development built for us — we own the IP. Full ownership. That's non-negotiable.", impatient1: "We're funding the development. Ownership follows funding.", impatient2: "If you want to retain IP, lower the price to reflect that.", hangup: "We own what we pay for. End of discussion. *click*" },
    coachingNotes: { whatWorks: "Separate custom configuration (theirs) from core platform code (yours). They own their data, their configurations, their custom reports. You retain the underlying technology.", commonMistake: "Giving away your IP. One client now owns the code you built your entire platform on.", winningMove: "Say: 'Peter, here's the split: all custom configurations, reports, and workflows built specifically for you — you own those outright. The underlying platform code and algorithms — we retain. You get perpetual usage rights. This is how Salesforce, Oracle, and every major vendor structures it.'" }
  },

  // 40. ELENA SANTOS — The Sunset Clause
  'elena-santos-vp-of-operations': {
    id: 'elena-santos-vp-of-operations',
    name: 'Elena Santos',
    role: 'VP of Operations',
    company: 'Pacifica Energy',
    industry: 'energy',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/full-negotiation/elena-santos-vp-of-operations.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Auto-Renewal Trap',
    scenarioDesc: "Elena is 44, Brazilian-American, and wants to remove the auto-renewal clause from your contract. She wants the deal to expire and require active re-signing every year. This kills your renewal rate and forces you to re-sell every 12 months. She frames it as 'keeping you honest.'",
    goal: 'Maintain auto-renewal with reasonable opt-out terms.',
    category: 'negotiation-tactics',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "Remove the auto-renewal clause. If you're delivering value, we'll renew. If not, we shouldn't be forced to.", impatient1: "Auto-renewal is a trap. I want the choice to leave cleanly every year.", impatient2: "I'm not signing anything that renews without my explicit approval.", hangup: "No auto-renewal, no signature. That's final. *click*" },
    coachingNotes: { whatWorks: "Keep auto-renewal but add a generous opt-out window — 60 or 90 days notice. She gets the safety valve, you get the renewal structure.", commonMistake: "Removing auto-renewal. Your churn rate will spike 3x because inertia is your best retention tool.", winningMove: "Say: 'Elena, here's the compromise: auto-renewal with a 90-day opt-out window. We'll send you a renewal notice at 120 days. You have a full quarter to evaluate and decide. If you want out, one email and you're free.'" }
  },

};